/** Transcript exports: plain text, SubRip, WebVTT, JSON. */

import { joinText, type Segment } from "./protocol.ts";

export type ExportFormat = "txt" | "srt" | "vtt" | "json";
export type SubtitleTrack = "target" | "source" | "both";

function clock(seconds: number, sep: "," | "."): string {
  const ms = Math.max(0, Math.round(seconds * 1000));
  const h = Math.floor(ms / 3_600_000);
  const m = Math.floor((ms % 3_600_000) / 60_000);
  const s = Math.floor((ms % 60_000) / 1000);
  const rest = ms % 1000;
  const pad = (n: number, w = 2) => String(n).padStart(w, "0");
  return `${pad(h)}:${pad(m)}:${pad(s)}${sep}${pad(rest, 3)}`;
}

function usable(segments: Segment[]): Segment[] {
  return segments.filter((s) => joinText(s.source) || joinText(s.target));
}

function cue(seg: Segment, track: SubtitleTrack): string {
  const source = joinText(seg.source);
  const target = joinText(seg.target);
  if (track === "source") return source;
  if (track === "target") return target || source;
  return [source, target].filter(Boolean).join("\n");
}

export function toText(segments: Segment[], name: (code: string) => string): string {
  return usable(segments)
    .map((seg) => {
      const stamp = clock(seg.startS, ".").slice(0, 8);
      const lines = [`[${stamp}] ${name(seg.lang)}: ${joinText(seg.source)}`];
      const target = joinText(seg.target);
      if (target) lines.push(`${" ".repeat(stamp.length + 3)}${name(seg.targetLang)}: ${target}`);
      if (seg.incomplete) lines.push(`${" ".repeat(stamp.length + 3)}(incomplete)`);
      return lines.join("\n");
    })
    .join("\n\n")
    .concat("\n");
}

export function toSrt(segments: Segment[], track: SubtitleTrack = "target"): string {
  return usable(segments)
    .map((seg, i) => `${i + 1}\n${clock(seg.startS, ",")} --> ${clock(Math.max(seg.endS, seg.startS + 0.5), ",")}\n${cue(seg, track)}\n`)
    .join("\n");
}

export function toVtt(segments: Segment[], track: SubtitleTrack = "target"): string {
  const body = usable(segments)
    .map((seg) => `${clock(seg.startS, ".")} --> ${clock(Math.max(seg.endS, seg.startS + 0.5), ".")}\n${cue(seg, track)}\n`)
    .join("\n");
  return `WEBVTT\n\n${body}`;
}

export function toJson(segments: Segment[], meta: Record<string, unknown>): string {
  const rows = usable(segments).map((seg) => ({
    start: seg.startS,
    end: seg.endS,
    lang: seg.lang,
    targetLang: seg.targetLang,
    source: joinText(seg.source),
    target: joinText(seg.target),
    final: seg.final,
    incomplete: seg.incomplete,
  }));
  return JSON.stringify({ ...meta, segments: rows }, null, 2);
}

export function exportFile(
  format: ExportFormat,
  segments: Segment[],
  options: { name: (code: string) => string; track?: SubtitleTrack; meta?: Record<string, unknown> },
): { body: string; mime: string; extension: string } {
  switch (format) {
    case "srt":
      return { body: toSrt(segments, options.track), mime: "application/x-subrip", extension: "srt" };
    case "vtt":
      return { body: toVtt(segments, options.track), mime: "text/vtt", extension: "vtt" };
    case "json":
      return { body: toJson(segments, options.meta ?? {}), mime: "application/json", extension: "json" };
    default:
      return { body: toText(segments, options.name), mime: "text/plain", extension: "txt" };
  }
}
